
import { supabase } from '../supabaseClient';
import { logger } from './logger'; // <--- ADICIONADO

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '/api'; // Caminho base da API

/**
 * Função de fetch encapsulada que adiciona o token de autenticação do Supabase
 * e trata a resposta.
 *
 * @param {string} url O caminho do endpoint da API (ex: '/dashboard/stats').
 * @param {object} options As opções do fetch (method, headers, body, etc.).
 * @returns {Promise<any>} Os dados da resposta em JSON.
 * @throws {Error} Lança um erro se a resposta da rede não for 'ok'.
 */
const apiFetch = async (url, options = {}) => {
  logger.info(`apiFetch: Iniciando para a URL: ${url}`);
  
  try {
    const headers = {
      'Content-Type': 'application/json',
      ...options.headers,
    };

    // Apenas busca a sessão se o cabeçalho de autorização não foi passado diretamente
    if (!headers['Authorization']) {
      logger.info('apiFetch: Cabeçalho de autorização não fornecido. Buscando sessão do Supabase...');
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError) {
        logger.error('apiFetch: Erro ao obter sessão do Supabase:', sessionError);
        throw new Error('Falha ao obter sessão do Supabase.');
      }

      if (session?.access_token) {
        headers['Authorization'] = `Bearer ${session.access_token}`;
        logger.info('apiFetch: Token de autorização do Supabase adicionado ao cabeçalho.');
      } else {
        logger.warn('apiFetch: Nenhuma sessão ou token de acesso encontrado. A requisição seguirá sem autenticação.');
      }
    } else {
      logger.info('apiFetch: Usando cabeçalho de autorização fornecido diretamente.');
    }

    const fetchURL = `${API_BASE_URL}${url}`;
    logger.info(`apiFetch: Realizando fetch para a URL: ${fetchURL}`);

    const response = await fetch(fetchURL, {
      ...options,
      headers,
    });

    logger.info(`apiFetch: Resposta recebida para ${url} com status: ${response.status}`);

    if (!response.ok) {
      const errorBody = await response.json().catch(() => ({ detail: response.statusText }));
      logger.error(`apiFetch: Erro na resposta da API para ${url}.`, { status: response.status, body: errorBody });
      throw new Error(`HTTP error! status: ${response.status}, message: ${errorBody.detail}`);
    }

    if (response.status === 204) {
        logger.info(`apiFetch: Resposta 204 (No Content) para ${url}. Retornando null.`);
        return null;
    }

    const responseData = await response.json();
    logger.info(`apiFetch: Resposta JSON processada com sucesso para ${url}.`);
    return responseData;

  } catch (error) {
    logger.error(`apiFetch: Erro catastrófico durante o fetch para ${url}.`, error);
    throw error;
  }
};

// Exporta métodos de conveniência
export const api = {
  get: (url, options) => apiFetch(url, { ...options, method: 'GET' }),
  post: (url, body, options) => apiFetch(url, { ...options, method: 'POST', body: JSON.stringify(body) }),
  put: (url, body, options) => apiFetch(url, { ...options, method: 'PUT', body: JSON.stringify(body) }),
  delete: (url, options) => apiFetch(url, { ...options, method: 'DELETE' }),
};

/**
 * Initiates the user registration process.
 * @param {string} name The user's name.
 * @param {string} email The user's email.
 * @param {string} password The user's password.
 * @returns {Promise<any>} The response from the API.
 */
export const initiateRegistration = async (userData) => {
  logger.info("Enviando dados de registro para a API:", userData);
  // Note: The /api/register/initiate endpoint does not require authentication.
  // We can call it directly without the Authorization header.
  return api.post('/register/initiate', userData);
};
